import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import AttachmentViewer from "../AttachmentViewer";
import type { RfqDialogsState } from "./useRfqDialogs";

/** The RFQ list's delete, blacklist and attachment dialogs (state: useRfqDialogs). */
export default function RfqDialogs({ dialogs }: { dialogs: RfqDialogsState }) {
  const {
    selectedCount,
    deleteDialogOpen,
    massDeletionMode,
    deletePending,
    rfqToBlacklist,
    blacklistDialogOpen,
    blacklistPending,
  } = dialogs;

  return (
    <>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={dialogs.cancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {massDeletionMode ? "Delete Selected RFQs" : "Delete RFQ"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {massDeletionMode
              ? `Are you sure you want to delete ${selectedCount} selected RFQ${selectedCount > 1 ? 's' : ''}? This action cannot be undone.`
              : "Are you sure you want to delete this RFQ? This action cannot be undone."
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={dialogs.cancelDelete}>
            Cancel
          </Button>
          <Button
            onClick={massDeletionMode ? dialogs.confirmMassDelete : dialogs.confirmDelete}
            color="error"
            variant="contained"
            disabled={deletePending}
          >
            {deletePending
              ? 'Deleting...'
              : massDeletionMode
                ? `Delete ${selectedCount} RFQ${selectedCount > 1 ? 's' : ''}`
                : 'Delete'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Blacklist Confirmation Dialog */}
      <Dialog
        open={blacklistDialogOpen}
        onClose={dialogs.cancelBlacklist}
        aria-labelledby="blacklist-dialog-title"
        aria-describedby="blacklist-dialog-description"
      >
        <DialogTitle id="blacklist-dialog-title">
          Blacklist Sender
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="blacklist-dialog-description">
            Are you sure you want to blacklist this sender?
            <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0, 0, 0, 0.04)', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Email:</strong> {rfqToBlacklist?.email}
              </Typography>
              <Typography variant="body2">
                <strong>Name:</strong> {rfqToBlacklist?.name}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 2, color: 'warning.main' }}>
              ⚠️ Future emails from this sender will not be processed as RFQs.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={dialogs.cancelBlacklist}>
            Cancel
          </Button>
          <Button
            onClick={dialogs.confirmBlacklist}
            color="warning"
            variant="contained"
            disabled={blacklistPending}
          >
            {blacklistPending ? 'Blacklisting...' : 'Blacklist Sender'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Attachment Viewer Dialog */}
      {dialogs.attachmentViewerRfqId && (
        <AttachmentViewer
          open={dialogs.attachmentViewerOpen}
          onClose={dialogs.closeAttachments}
          rfqId={dialogs.attachmentViewerRfqId}
          rfqSubject={dialogs.attachmentViewerRfqSubject}
        />
      )}
    </>
  );
}
